import React, { useMemo, useState } from 'react';
import type { AssignmentCoverImage, RoutineAssignmentItem } from '../../types/assignmentPointsNote';
import { listRoutineDateSummaries } from '../../utils/assignmentPointsNoteProgress';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';

type Props = {
  item: RoutineAssignmentItem;
  coverImage?: AssignmentCoverImage | null;
  isShared?: boolean;
  /** 설정·복제·삭제 버튼 묶음 */
  actions: React.ReactNode;
  /** 날짜·포인트·보상 메타 */
  meta: React.ReactNode;
  days: string;
  memberIdSet?: Set<string> | null;
  activeDate?: string | null;
  rewardProcessing?: boolean;
  onOpenMonitoring: () => void;
  onGrantReward?: () => void;
  onSelectDate: (date: string) => void;
  onAddCustomDate: () => void;
};

const PAGE_SIZE = 10;

const RoutineAssignmentDashboard: React.FC<Props> = ({
  item,
  coverImage,
  isShared,
  actions,
  meta,
  days,
  memberIdSet,
  activeDate,
  rewardProcessing = false,
  onOpenMonitoring,
  onGrantReward,
  onSelectDate,
  onAddCustomDate,
}) => {
  const [sort, setSort] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);

  const summaries = useMemo(() => listRoutineDateSummaries(item, { sort, memberIdSet }), [item, sort, memberIdSet]);
  const totalPages = Math.max(1, Math.ceil(summaries.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = summaries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const completeDays = useMemo(
    () => summaries.filter((r) => r.hasRecords && r.pendingCount === 0 && r.totalStudents > 0).length,
    [summaries]
  );

  return (
    <div className="cp-apn-block-wrap cp-chl-dash is-routine" data-tutorial-apn="routine-detail">
      <header className={`cp-chl-dash-hero${coverImage ? ' has-cover' : ''}`}>
        {coverImage ? (
          <div className="cp-chl-dash-hero-cover" aria-hidden>
            <CachedAssignmentCoverImage image={coverImage} className="cp-chl-dash-hero-cover-img" fallback={null} />
          </div>
        ) : null}
        <div className="cp-chl-dash-hero-top">
          <span className="cp-chl-dash-badge">루틴</span>
          {isShared ? <span className="cp-chl-dash-badge is-ghost">공유과제</span> : null}
          <span className="cp-chl-dash-hero-tools">{actions}</span>
        </div>
        <h3 className="cp-chl-dash-title">{item.title || '루틴 과제'}</h3>
        <div className="cp-chl-dash-meta">{meta}</div>
        <div className="cp-chl-dash-hero-chips">
          <span className="cp-chl-dash-hero-stat">{days || '요일 없음'}</span>
          {summaries.length > 0 ? (
            <span className="cp-chl-dash-hero-stat">
              완료 {completeDays}/{summaries.length}일
            </span>
          ) : null}
        </div>
      </header>

      <div className="cp-chl-dash-btns">
        <button
          type="button"
          className="cp-chl-dash-btn is-primary"
          data-tutorial-apn="routine-reach"
          onClick={onOpenMonitoring}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <rect x="3" y="12" width="4" height="9" rx="1.2" fill="currentColor" />
            <rect x="10" y="7" width="4" height="14" rx="1.2" fill="currentColor" />
            <rect x="17" y="3" width="4" height="18" rx="1.2" fill="currentColor" />
          </svg>
          멤버별 도달률
        </button>
        {item.rewardEnabled ? (
          <button
            type="button"
            className="cp-chl-dash-btn"
            data-tutorial-apn="routine-reward"
            disabled={rewardProcessing}
            onClick={onGrantReward}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path d="M3 10h18v11H3V10zm-1-4h20v4H2V6z" fill="currentColor" />
            </svg>
            {rewardProcessing ? '보상 처리 중...' : '보상 지급'}
          </button>
        ) : null}
        <button type="button" className="cp-chl-dash-btn" onClick={onAddCustomDate}>
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" fill="currentColor" />
          </svg>
          커스텀 날짜 등록
        </button>
      </div>

      <div className="cp-chl-dash-routine-bar">
        <button
          type="button"
          className="cp-chl-dash-routine-sort"
          onClick={() => {
            setSort((v) => (v === 'desc' ? 'asc' : 'desc'));
            setPage(1);
          }}
        >
          날짜별 현황 · {sort === 'desc' ? '최신순' : '오래된순'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d={sort === 'desc' ? 'M6 9l6 6 6-6' : 'M6 15l6-6 6 6'}
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {totalPages > 1 ? (
          <span className="cp-chl-dash-routine-pager">
            <button
              type="button"
              disabled={currentPage <= 1}
              aria-label="이전 페이지"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 6l-6 6 6 6"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span>
              {currentPage}/{totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              aria-label="다음 페이지"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </span>
        ) : null}
      </div>

      <div className="cp-chl-dash-list is-routine">
        {pageRows.length === 0 ? <p className="cp-chl-dash-empty">표시할 날짜가 없어요.</p> : null}
        {pageRows.map((row) => {
          const complete = row.hasRecords && row.pendingCount === 0 && row.totalStudents > 0;
          const confirmed = Math.max(0, row.totalStudents - row.pendingCount);
          const pct = row.hasRecords && row.totalStudents > 0 ? Math.round((confirmed / row.totalStudents) * 100) : 0;
          const [, mm, dd] = row.date.split('-');
          return (
            <button
              key={row.date}
              type="button"
              className={`cp-chl-dash-date${complete ? ' is-complete' : ''}${
                activeDate === row.date ? ' is-active' : ''
              }${row.hasRecords ? '' : ' is-empty'}`}
              onClick={() => onSelectDate(row.date)}
            >
              <span className="cp-chl-dash-date-day">
                {Number(mm)}.{Number(dd)}
                <small>{row.dayOfWeek}</small>
              </span>
              {row.isCustomDate ? <span className="cp-chl-dash-date-tag">커스텀</span> : null}
              <span className="cp-chl-dash-date-progress" aria-hidden>
                <span style={{ width: `${pct}%` }} />
              </span>
              <span className="cp-chl-dash-date-status">
                {row.hasRecords ? (complete ? '모두 확인' : `확인 ${confirmed}/${row.totalStudents}`) : '기록 없음'}
              </span>
              <svg
                className="cp-chl-dash-card-chevron"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RoutineAssignmentDashboard;
